/**
 * Beta Journey Service
 * Integrates with beta.redheli.com for journey management
 * Option A Architecture: Beta handles all journey data
 */

export interface JourneyData {
  userId: string;
  step: number;
  progress: {
    step1: boolean;
    step2: boolean;
    step3: boolean;
    step4: boolean;
    step5: boolean;
    step6: boolean;
  };
  data?: any;
}

export class BetaJourneyService {
  private betaUrl = import.meta.env.VITE_BETA_URL || 'https://beta.redheli.com';
  
  /**
   * Launch the journey experience in Beta
   * Redirects user to Beta platform with return URL
   */
  async launchJourney(userId: string, email?: string): Promise<void> {
    // Create return URL for coming back to Hover
    const returnUrl = encodeURIComponent(window.location.origin + '/portal');
    
    // Build Beta URL with user context
    const params = new URLSearchParams({
      userId,
      email: email || '',
      returnUrl,
      source: 'hover',
      timestamp: Date.now().toString()
    });
    
    // Redirect to Beta journey
    window.location.href = `${this.betaUrl}/journey?${params.toString()}`;
  }
  
  /**
   * Launch journey in iframe (embedded mode)
   */
  getEmbeddedUrl(userId: string, email?: string): string {
    const returnUrl = encodeURIComponent(window.location.origin + '/portal');
    
    const params = new URLSearchParams({
      userId,
      email: email || '',
      returnUrl,
      source: 'hover',
      embedded: 'true',
      timestamp: Date.now().toString()
    });
    
    return `${this.betaUrl}/journey?${params.toString()}`;
  }
  
  /**
   * Get journey data from Beta API
   * This would be used if we want to display progress in Hover
   */
  async getJourneyData(userId: string): Promise<JourneyData | null> {
    try {
      const response = await fetch(`${this.betaUrl}/api/journey/${userId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-Source': 'hover'
        },
        credentials: 'include'
      });
      
      if (!response.ok) {
        console.error('Failed to fetch journey data from Beta');
        return null;
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error fetching journey data:', error);
      return null;
    }
  }
  
  /**
   * Check if user has completed journey
   */
  async checkJourneyCompletion(userId: string): Promise<boolean> {
    const data = await this.getJourneyData(userId);
    if (!data) return false;
    
    const { progress } = data;
    return progress.step1 && progress.step2 && progress.step3 && 
           progress.step4 && progress.step5 && progress.step6;
  }
  
  /**
   * Export journey data from Beta
   */
  async exportJourneyData(userId: string, format: 'json' | 'excel' = 'json'): Promise<Blob | null> {
    try {
      const response = await fetch(`${this.betaUrl}/api/journey/export/${userId}?format=${format}`, {
        method: 'GET',
        headers: {
          'X-Source': 'hover'
        },
        credentials: 'include'
      });
      
      if (!response.ok) {
        console.error('Failed to export journey data from Beta');
        return null;
      }
      
      return await response.blob();
    } catch (error) {
      console.error('Error exporting journey data:', error);
      return null;
    }
  }
}

// Export singleton instance
export const betaJourneyService = new BetaJourneyService();